import { z } from 'zod';

import { baseOrganizationEntitySchema } from './organizationContract';
import {
  Agent,
  ChatModel,
  ResponseLengthPreset,
  TonePreset,
  ChatPlacementPosition,
  AgentType,
  Gender,
  ThemePreset,
  SourcesFormat,
  STTProvider,
  ElevenLabsVoices,
} from '../enums';

export const chatThemeSchema = z
  .object({
    preset: z.nativeEnum(ThemePreset).optional(),
    primary: z.string().nullish(),
    primaryFg: z.string().nullish(),
    secondary: z.string().nullish(),
    secondaryFg: z.string().nullish(),
    backgroundSecondary: z.string().nullish(),
    background: z.string().nullish(),
    foreground: z.string().nullish(),
    placeholder: z.string().nullish(),
    logo: z.string().nullish(),
    logoBubble: z.string().nullish(),
  })
  .strict();

export type ChatThemeConfig = z.infer<typeof chatThemeSchema>;

export const agentConfigSchema = z
  .object({
    /**
     * This field contains additional agent personalization like,
     * company description, name, it's role or additional directives
     */
    personalization: z.string().optional(),
    systemPrompt: z.string().optional(),
    gender: z.nativeEnum(Gender).optional(),
    hybridAlpha: z.number().nonnegative().optional(),
    rewriteRetries: z.number().int().nonnegative().optional(),
    historyLimit: z.number().int().nonnegative().optional(),
    retrievalCount: z.number().int().nonnegative().optional(),
    contextHistoryLimit: z.number().int().nonnegative().optional(),
    rerankCount: z.number().int().nonnegative().optional(),
    weightRerankCoefficient: z.number().nonnegative().optional(),
    rerankCoefficient: z.number().nonnegative().optional(),
    rerank: z.boolean().optional(),
    imageSupport: z.boolean().optional(),
    showSources: z.boolean().optional(),
    minScore: z.number().optional(),
    tonePreset: z.nativeEnum(TonePreset).optional(),
    responseLengthPreset: z.nativeEnum(ResponseLengthPreset).optional(),
    hasWebSearch: z.boolean().optional(),
    webSearchMaxResults: z.number().int().nonnegative().optional(),
    webSearchUrls: z.array(z.string()).optional(),
    showFollowUpQuestions: z.boolean().optional(),
    language: z.string().optional(),
  })
  .strict();

export type AgentConfig = z.infer<typeof agentConfigSchema>;

export const voiceConfigSchema = z
  .object({
    keywords: z.array(z.string()).optional(),
    endpointing: z.number().min(10).optional(),
    loadingMessages: z.array(z.string()).optional(),
    loadingTimeout: z.number().min(10).optional(),
    silenceMessages: z.array(z.string()).optional(),
    silenceTimeout: z.number().min(10).optional(),
    sttProvider: z.nativeEnum(STTProvider).optional(),
    twilio: z
      .object({
        accountSid: z.string(),
        authToken: z.string(),
        // Validate E.164 format, with whitespace allowed
        // number: z.string().regex(/^\+[1-9]\d{1,14}$/), // TODO fixme, form can't be saved with empty strings since it fails validation
        number: z.string(),
      })
      .nullish(),
    elevenLabs: z
      .object({
        voiceId: z.nativeEnum(ElevenLabsVoices).optional(),
      })
      .nullish(),
  })
  .strict();

export type VoiceConfig = z.infer<typeof voiceConfigSchema>;

export const omnichannelConfigSchema = z
  .object({
    accessKey: z.string().nullish(),
    workspaceId: z.string().nullish(),
  })
  .strict();

export type OmnichannelConfig = z.infer<typeof omnichannelConfigSchema>;

export const chatbotConfigSchema = z
  .object({
    systemAvatar: z.string().optional(),
    initialMessage: z.string().optional(),
    bubbleTitle: z.string().optional(),
    bubbleDescription: z.string().optional(),
    requireAcknowledgement: z.boolean().optional(),
    disclaimerContent: z.string().optional(),
    showPopup: z.boolean().optional(),
    showPopupMobile: z.boolean().optional(),
    showFeedbackButtons: z.boolean().optional(),
    sourcesFormat: z.nativeEnum(SourcesFormat).optional(),
    placement: z
      .object({
        position: z.nativeEnum(ChatPlacementPosition).optional(),
        offsetX: z.number().int().optional(),
        offsetY: z.number().int().optional(),
      })
      .optional(),
  })
  .optional();

export type ChatbotConfig = z.infer<typeof chatbotConfigSchema>;

export const chatSchema = baseOrganizationEntitySchema.extend({
  name: z.string().max(100),
  agent: z.nativeEnum(Agent).optional(),
  themeConfig: chatThemeSchema.optional(),
  agentType: z.nativeEnum(AgentType),
  voiceConfig: voiceConfigSchema.optional(),
  omnichannelConfig: omnichannelConfigSchema.optional(),
  chatbotConfig: chatbotConfigSchema,
  agentConfig: agentConfigSchema,
  overrides: z.boolean().optional(),
  model: z.nativeEnum(ChatModel).optional(),
  smallModel: z.nativeEnum(ChatModel).optional(),
});

export const createChatSchema = chatSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    organization: true,
  })
  .strict();

export const updateChatSchema = chatSchema
  .partial()
  .required({
    id: true,
  })
  .omit({
    organization: true,
  })
  .strict();

export const exportXlsxSchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export type ChatDTO = z.infer<typeof chatSchema>;
export type CreateChatDTO = z.infer<typeof createChatSchema>;
export type UpdateChatDTO = z.infer<typeof updateChatSchema>;

// Streamed chunk types
export const streamInitChunkSchema = z.object({
  type: z.literal('init'),
  id: z.string(),
});

export const streamTextChunkSchema = z.object({
  type: z.literal('text'),
  content: z.string(),
});

export const streamToolChunkSchema = z.object({
  type: z.literal('tool'),
  id: z.string(),
  index: z.number().int().nonnegative(),
  name: z.string(),
  args: z.string(),
});

export const streamChunkSchema = z.union([
  streamInitChunkSchema,
  streamTextChunkSchema,
  streamToolChunkSchema,
]);

export type StreamInitChunk = z.infer<typeof streamInitChunkSchema>;
export type StreamTextChunk = z.infer<typeof streamTextChunkSchema>;
export type StreamToolChunk = z.infer<typeof streamToolChunkSchema>;
export type StreamChunk = z.infer<typeof streamChunkSchema>;
